import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import OberonDialog from '~components/OberonDialog';

import { CreateExclusionList } from '../../domain';

interface Form {
  name: string;
  description: string;
}

interface CreateExclusionListModalProps {
  open: boolean;
  submitting: boolean;
  onAccept: (data: CreateExclusionList) => Promise<void>;
  onClose: () => void;
}

const CreateExclusionListModal = ({ open, submitting, onAccept, onClose }: CreateExclusionListModalProps) => {
  const isLoading = submitting;
  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
  } = useForm<Form>({
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });

  // Handles all async based actions
  useEffect(() => {
    // Reset form on close
    return function cleanupCreateExclusionListModal() {
      reset();
    };
  }, [open]);

  const onSubmit = handleSubmit(async (data: Form) => {
    try {
      await onAccept({
        name: data.name.trim(),
        description: data.description.trim(),
      });
    } catch (e) {
      // Do nothing, catch error to prevent form reset on failed action
      return;
    }

    reset();
  });

  return (
    <OberonDialog
      open={open}
      onSubmit={onSubmit}
      onClose={onClose}
      title='Create Exclusion List'
      content={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='name'
              control={control}
              rules={{
                required: 'Name is required.',
              }}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Name'
                  disabled={submitting}
                  required={true}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  {...field}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='description'
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant='outlined'
                  label='Description'
                  disabled={submitting}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
      }
      actionFooter={
        <>
          <Button variant='text' disabled={isLoading} onClick={onClose}>
            Close
          </Button>

          <LoadingButton
            type='submit'
            variant='contained'
            disableElevation
            color='primary'
            disabled={isLoading}
            loading={isLoading}>
            Create
          </LoadingButton>
        </>
      }
    />
  );
};

export default CreateExclusionListModal;
